import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil, mergeMap, tap } from 'rxjs/operators';
import { SessionDataMapped } from '@airtradeit/authentication';

import { LanguagesEnumType } from '@enums/languages.enum';
import { GlobalConstant } from '@constants/global.constant';
import { Language } from '@constants/partials/languages.constant';
import { LocalStorageService } from '@shared/services/local-storage/local-storage.service';
import { AuthService } from '@shared/services/auth/auth.service';
import { ContentService } from '@shared/services/content/content.service';
import { Router } from '@angular/router';
import { UrlPathnameEnum } from '@enums/url-pathname.enum';

@Component({
  selector: 'app-header-languages',
  templateUrl: './header-languages.component.html',
  styleUrls: ['./header-languages.component.scss'],
})
export class HeaderLanguagesComponent implements OnInit, OnDestroy {
  languages: Language[];
  currentLanguage: LanguagesEnumType;
  private unsubscribeAll$: Subject<void> = new Subject();

  constructor(
    @Inject(GlobalConstant) private _globalConstant: GlobalConstant,
    @Inject(AuthService) private _authService: AuthService,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(TranslateService) private _translateService: TranslateService,
    @Inject(ContentService) private _contentService: ContentService,
    private _router: Router
  ) {
    this.languages = this._globalConstant.languages.availableLanguages;
  }

  ngOnInit(): void {
    this.currentLanguage = this._translateService.getDefaultLang() as LanguagesEnumType;
    this.onLangChange().subscribe();
  }

  onLangChange(): Observable<SessionDataMapped> {
    return this._translateService.onLangChange.pipe(
      takeUntil(this.unsubscribeAll$),
      mergeMap(({ lang }: LangChangeEvent) => {
        this.currentLanguage = lang as LanguagesEnumType;
        this._localStorageService.setCulture(lang);
        this._contentService.getErrorMessages();

        return this._authService.updateAndStoreSession({ CultureCode: lang }).pipe(
          tap(() => {
            const currentUrl = this._router.url;

            /* Exceptional case: 
              Update price formatting and ancillary services 
              from BFF after a page reload, 
              that maybe extended for all routes within application */
            if (currentUrl.includes(UrlPathnameEnum.CommonOrder)) {
              window.location.reload();
            }
          })
        );
      })
    );
  }

  handleLangChange(lang: LanguagesEnumType): void {
    this._translateService.use(lang as string);
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }
}
